import { inject, Observer } from "mobx-react"
import Head from "next/head"
import React from "react"
import styled, { css } from "styled-components"

import CtaLink from "~/components/atoms/CtaLink"
import Link from "~/components/atoms/Link"
import Pc from "~/components/atoms/Pc"
import Sp from "~/components/atoms/Sp"
import TransitionLink from "~/components/atoms/TransitionLink"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import CategoryCard from "~/components/organisms/CategoryCard"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import User from "~/components/organisms/User"
import { Color, IsMobile, IsPC, RadiusSize, Shadow, SpacingSize, Z } from "~/consts/style-mixins"
import { Company } from "~/entities/Company"
import { Event } from "~/entities/Event"
import { LongInternship } from "~/entities/LongInternship"
import { Magazine } from "~/entities/Magazine"
import { NewGradJob } from "~/entities/NewGradJob"
import RecruitmentList from "~/observers/RecruitmentList"
import TabBar from "~/observers/TabBar"
import { internalLink, staticPath } from "~/services/helper"
import RootStore from "~/stores/RootStore"
import UserStore from "~/stores/UserStore"

interface Props {
  userStore: UserStore
  companies: Company[]
  newGradJobs: NewGradJob[]
  longInternships: LongInternship[]
  events: Event[]
  magazines: Magazine[]
}

@inject("userStore")
export default class Top extends React.Component<Props> {
  static async getInitialProps({ initialStoreData }: { initialStoreData: RootStore }) {
    const { companyStore, newGradJobStore, longInternshipStore, eventStore, magazineStore } = initialStoreData

    const companies = await companyStore.fetch(7)
    const newGradJobs = await newGradJobStore.fetchByLimited(3)
    const longInternships = await longInternshipStore.fetch(3)
    const { events } = await eventStore.fetch({ number: 3 })
    const magazines = await magazineStore.fetch(3)
    return {
      companies,
      newGradJobs,
      longInternships,
      events,
      magazines,
    }
  }

  render() {
    return (
      <div>
        <Head>
          <title>インターンシップ・就職活動/新卒採用情報ならInfra（インフラ）</title>
          <meta
            name="description"
            content="Infra（インフラ）は大学1, 2年など早期から参加できる長期インターンシップや短期インターンの募集情報また新卒イベント・説明会や企業の採用情報を提供しています。学生の成長・キャリア構築ため、インターンから就職までをサポートします。"
          />
          <meta property="og:title" content="インターンシップ・就職活動/新卒採用情報ならInfra（インフラ）" />
          <meta
            property="og:description"
            content="Infra（インフラ）は大学1, 2年など早期から参加できる長期インターンシップや短期インターンの募集情報また新卒イベント・説明会や企業の採用情報を提供しています。学生の成長・キャリア構築ため、インターンから就職までをサポートします。"
          />
          <meta property="og:image" content={`${process.env.APP_HOST + staticPath("/static/images/ogp.png")}`} />
        </Head>
        <Header showTab logoType="Plain" />
        <StyledContainerWrapper>
          <StyledMainVisual>
            <StyledMainVisualInner>
              <StyledHeadingWrapper>
                <StyledSubTitleTypography type="SubHero">就活に学年は関係ない</StyledSubTitleTypography>
                <StyledHeroTypography type="Hero">今の挑戦が次に繋がる</StyledHeroTypography>
              </StyledHeadingWrapper>
            </StyledMainVisualInner>
          </StyledMainVisual>
          <StyledNextActionWrapper>
            <Container size="Large">
              <StyledHeadingTypography type="Hero">さあ次は何しよう？</StyledHeadingTypography>
              <Pc>
                <StyledNextActionCardWrapper>
                  <StyledNextActionCardWrapperRow>
                    <StyledNextActionCategoryCard
                      thumbnailImagePath={staticPath("/static/images/new-grad-job-thumb.png")}
                      title="新卒スカウト"
                      upperDescription="学生時代の経験をアピールして"
                      underDescription="企業からオファーを獲得しよう"
                      link={internalLink("/new-grad-scout")}
                    />
                    <StyledNextActionCategoryCard
                      thumbnailImagePath={staticPath("/static/images/long-internship-thumb.png")}
                      title="長期インターン"
                      upperDescription="実際のビジネスに挑戦して"
                      underDescription="スキルや経験を得る"
                      link={`${process.env.INFRA_INTERN_URL}`}
                    />
                  </StyledNextActionCardWrapperRow>
                  <StyledNextActionCardWrapperRow className="action-card-bottom">
                    <StyledNextActionCategoryCard
                      thumbnailImagePath={staticPath("/static/images/event-top-thumb.png")}
                      title="イベント・説明会"
                      upperDescription="自分のキャリアを"
                      underDescription="見つけよう、深く知ろう"
                      link={internalLink("/events")}
                    />
                  </StyledNextActionCardWrapperRow>
                </StyledNextActionCardWrapper>
              </Pc>
              <Sp>
                <StyledNextActionCardWrapper>
                  <StyledNextActionCardWrapperRow>
                    <StyledNextActionCategoryCard
                      thumbnailImagePath={staticPath("/static/images/new-grad-job-thumb.png")}
                      title="新卒スカウト"
                      upperDescription="学生時代の経験をアピールして"
                      underDescription="企業からオファーを獲得しよう"
                      link={internalLink("/new-grad-scout")}
                    />
                  </StyledNextActionCardWrapperRow>
                  <StyledNextActionCardWrapperRow>
                    <StyledNextActionCategoryCard
                      thumbnailImagePath={staticPath("/static/images/long-internship-thumb.png")}
                      title="長期インターン"
                      upperDescription="実際のビジネスに挑戦して"
                      underDescription="スキルや経験を得る"
                      link={`${process.env.INFRA_INTERN_URL}/intern-list`}
                    />
                  </StyledNextActionCardWrapperRow>
                  <StyledNextActionCardWrapperRow>
                    <StyledNextActionCategoryCard
                      thumbnailImagePath={staticPath("/static/images/event-top-thumb.png")}
                      title="イベント・説明会"
                      upperDescription="自分のキャリアを"
                      underDescription="見つけよう、深く知ろう"
                      link={internalLink("/events")}
                    />
                  </StyledNextActionCardWrapperRow>
                </StyledNextActionCardWrapper>
              </Sp>
            </Container>
          </StyledNextActionWrapper>

          <StyledScoutWrapper>
            <Container size="Large">
              <StyledScoutHeadingWrapper>
                <StyledDecorationTypography type="SubHeading">プロフィールを書くだけ</StyledDecorationTypography>
                <StyledScoutHeadingTypography type="Hero">企業からスカウトが届く！</StyledScoutHeadingTypography>
              </StyledScoutHeadingWrapper>
              {/* Storeの変更をobserveして表示を変更したいのでObserverコンポーネントを使用 */}
              <Observer>
                {() =>
                  this.props.userStore.authed ? (
                    <>
                      <StyledBodyTypography type="Body">
                        新卒、短期・長期インターン、
                        <br className="pc-hide" />
                        イベント・説明会へのスカウトが届きます！
                        <br />
                        詳しくプロフィールを書き、企業にアピールしよう！
                      </StyledBodyTypography>
                      <StyledCtaLink href={internalLink("/profile")} size="Medium" className="gtm_scout-profile-edit">
                        プロフィールを入力する
                      </StyledCtaLink>
                    </>
                  ) : (
                    <>
                      <StyledBodyTypography type="Body">
                        新卒、短期・長期インターン、
                        <br className="pc-hide" />
                        イベント・説明会へのスカウトが届きます！
                        <br />
                        さっそく登録して、プロフィールを入力しよう！
                      </StyledBodyTypography>
                      <StyledCtaLink
                        href={internalLink("/auth/register?redirect_after_auth=/next")}
                        size="Medium"
                        className="gtm_scout-register"
                      >
                        新規登録する
                      </StyledCtaLink>
                    </>
                  )
                }
              </Observer>
            </Container>
          </StyledScoutWrapper>

          <StyledPickupWrapper>
            <Container size="Large">
              <StyledHeadingTypography type="Hero">ピックアップ</StyledHeadingTypography>
              {/* 長期インターン */}
              <StyledEachCategoryWrapper>
                <StyledHeadingAndMoreLink>
                  <Typography type="SubHero">長期インターン</Typography>
                  <StyledMoreLink size="Large" href={`${process.env.INFRA_INTERN_URL}/intern-list`} icon="Next">
                    もっと見る
                  </StyledMoreLink>
                </StyledHeadingAndMoreLink>
                <StyledItemListWrapper>
                  <StyledRecruitmentList type="longInternship" recruitments={this.props.longInternships} />
                </StyledItemListWrapper>
              </StyledEachCategoryWrapper>
              {/* イベント・説明会 */}
              <StyledEachCategoryWrapper>
                <StyledHeadingAndMoreLink>
                  <Typography type="SubHero">イベント・説明会</Typography>
                  <StyledMoreLink size="Large" href={internalLink("/events")} icon="Next">
                    もっと見る
                  </StyledMoreLink>
                </StyledHeadingAndMoreLink>
                <StyledItemListWrapper>
                  <StyledRecruitmentList type="event" recruitments={this.props.events} />
                </StyledItemListWrapper>
              </StyledEachCategoryWrapper>
            </Container>
          </StyledPickupWrapper>
        </StyledContainerWrapper>
        <Sp>
          <TabBar selectedTabIndex={0} />
        </Sp>
        <StyledAttentionCompany>
          <Container size="Large">
            <StyledHeadingTypography type="Hero">注目の会社</StyledHeadingTypography>
            <Pc>
              <StyledCompanyList>
                {this.props.companies.map((company, index) => {
                  const companyName = company.name.replace("株式会社", "")
                  return (
                    <StyledCompanyItem href={internalLink(`/companies/${company.id}`)} key={index}>
                      <User
                        type="xLarge"
                        name={companyName}
                        iconPath={company.logoImgUrl}
                        description={company.overview}
                      />
                    </StyledCompanyItem>
                  )
                })}
              </StyledCompanyList>
              <TransitionLink href={internalLink("/companies")} size="xLarge" icon="Next">
                会社をもっと見る
              </TransitionLink>
            </Pc>
            <Sp>
              <StyledCompanyList>
                {this.props.companies.slice(0, 3).map((company, index) => {
                  return (
                    <StyledCompanyItem href={internalLink(`/companies/${company.id}`)} key={index}>
                      <User
                        type="xLarge"
                        name={company.name}
                        iconPath={company.logoImgUrl}
                        description={company.overview}
                      />
                    </StyledCompanyItem>
                  )
                })}
              </StyledCompanyList>
              <TransitionLink href={internalLink("/companies")} size="Large" icon="Next">
                会社をもっと見る
              </TransitionLink>
            </Sp>
          </Container>
        </StyledAttentionCompany>
        <Footer />
      </div>
    )
  }
}

const StyledContainerWrapper = styled.div`
  width: 100%;
  background-color: ${Color.White};
`

const StyledMainVisual = styled.div`
  width: 100%;
  height: 250px;
  background-image: url(${staticPath("/static/images/main-visual-sp.png")});
  background-position: center;
  background-size: cover;
  position: relative;
  ${css`
    ${IsPC(`
        height: 400px;
        background-image: url(${staticPath("/static/images/main-visual-pc.png")});
      `)}
  `}
`

const StyledMainVisualInner = styled.div`
  position: relative;
  height: 100%;
  max-width: 1032px;
  margin: 0 auto;
`

const StyledHeadingWrapper = styled.div`
  padding: ${SpacingSize.M}px;
  border-radius: ${RadiusSize.Medium}px;
  background-color: ${Color.White};
  ${Shadow.Deep};
  position: absolute;
  left: 16px;
  ${IsPC(css`
    bottom: 24px;
  `)}
  ${IsMobile(css`
    padding: 12px;
    bottom: 16px;
  `)}
`

const StyledSubTitleTypography = styled(Typography)`
  line-height: 100%;
  color: ${Color.Gray};
  ${IsPC(css`
    margin-bottom: 8px;
  `)}
  ${IsMobile(css`
    font-size: 14px;
    margin-bottom: 4px;
  `)}
`

const StyledHeroTypography = styled(Typography)`
  line-height: 100%;
  ${IsMobile(css`
    font-size: 24px;
  `)}
  color: ${Color.Primary};
`

const StyledAttentionCompany = styled.div`
  padding: 48px 0;
  text-align: center;
  background-color: ${Color.BackgroundGray};
  ${IsMobile(css`
    padding: 32px 0;
  `)}
`

const StyledHeadingTypography = styled(Typography)`
  margin-bottom: 24px;
  text-align: center;
  ${IsMobile(css`
    margin-bottom: 20px;
    font-size: 24px;
  `)}
`

const StyledCompanyList = styled.div`
  display: flex;
  margin: 0 -8px;
  ${IsPC(css`
    padding: 0 0 32px;
  `)}
  ${IsMobile(css`
    padding: 0 0 24px;
  `)}
`

const StyledCompanyItem = styled(Link)`
  padding: 0 8px;
  ${IsPC(css`
    width: 14.28%;
  `)}
  ${IsMobile(css`
    width: 33.3%;
  `)}
`

const StyledNextActionWrapper = styled.div`
  padding: 48px 0;
  background-color: ${Color.White};
  ${IsMobile(css`
    padding: 32px 0;
  `)}
`

const StyledNextActionCardWrapper = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  align-items: center;
  margin: 0 -8px;
`

const StyledNextActionCardWrapperRow = styled.div`
  width: 100%;
  margin-bottom: ${SpacingSize.M}px;
  display: flex;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
`

const StyledNextActionCategoryCard = styled(CategoryCard)`
  flex: 1;
  padding: 0 8px;
`

const StyledFullWidthNextActionCategoryCard = styled(CategoryCard)`
  width: 100%;
  padding: 0 8px;
`

const StyledPickupWrapper = styled.div`
  padding: 48px 0 48px;
  ${IsMobile(css`
    padding: 32px 0 32px;
  `)}
`

const StyledEachCategoryWrapper = styled.div`
  ${IsPC(css`
    &:not(:last-child) {
      margin-bottom: 48px;
    }
  `)}
  ${IsMobile(css`
    &:not(:last-child) {
      margin-bottom: 32px;
    }
  `)}
`

const StyledHeadingAndMoreLink = styled.div`
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const StyledMoreLink = styled(TransitionLink)`
  margin-bottom: 4px;
`

const StyledItemListWrapper = styled.div`
  display: flex;
  ${IsPC(css`
    margin: 0 -8px;
  `)}
  ${IsMobile(css`
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
    margin: 0 -16px;
    padding: 0 16px;
    &::-webkit-scrollbar {
      display: none;
    }
  `)}
`

const StyledRecruitmentList = styled(RecruitmentList)`
  ${IsPC(css`
    width: 33.3%;
    padding: 0 8px;
  `)}
  ${IsMobile(css`
    min-width: 296px;
    padding-right: 16px;
  `)}
`

const StyledScoutWrapper = styled.div`
  position: relative;
  padding: 48px 0;
  background-color: ${Color.BackgroundGray};
  text-align: center;
  ${IsMobile(css`
    padding: 32px 0;
  `)}
`

const StyledScoutHeadingWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
`

const StyledScoutHeadingTypography = styled(Typography)`
  position: relative;
  z-index: ${Z.Child};
  margin-bottom: 8px;
  text-align: center;
  ${IsMobile(css`
    font-size: 24px;
  `)}
  &:before {
    z-index: -1;
    position: absolute;
    bottom: -8px;
    left: -32px;
    content: url(${staticPath("/static/images/scout_decoration.svg")});
  }
`

const StyledBodyTypography = styled(Typography)`
  color: ${Color.Gray};
  line-height: 160%;
  ${IsMobile(css`
    font-size: 14px;
  `)}
  .pc-hide {
    ${IsPC(css`
      display: none;
    `)}
  }
`

const StyledCtaLink = styled(CtaLink)`
  max-width: 280px;
  margin: 16px auto auto;
`

const StyledDecorationTypography = styled(Typography)`
  position: relative;
  margin: auto auto 2px;
  color: ${Color.Primary};
  ${IsMobile(css`
    font-size: 14px;
  `)}
  &:before {
    position: absolute;
    bottom: 6px;
    left: -16px;
    content: "";
    width: 16px;
    height: 2px;
    background: ${Color.Primary};
    transform: rotate(60deg);
    ${IsMobile(css`
      width: 14px;
    `)}
  }
  &:after {
    position: absolute;
    bottom: 6px;
    right: -16px;
    content: "";
    width: 16px;
    height: 2px;
    background: ${Color.Primary};
    transform: rotate(-60deg);
    ${IsMobile(css`
      width: 14px;
    `)}
  }
`
